import { useState } from "react";

import {
  ApiKeysService,
  type ApiKeyDetail,
  type DisableApiKeyRequest,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface ApiKeyDisableDialogProps {
  apiKey: ApiKeyDetail | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Disable — reversible state. Reason is optional but encouraged
 * (lands in the audit chain). Re-enabling is a separate action.
 */
export function ApiKeyDisableDialog({
  apiKey,
  onClose,
  onSuccess,
}: ApiKeyDisableDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  const { mutate, isLoading, error, reset } = useMutation<
    DisableApiKeyRequest,
    ApiKeyDetail
  >((body) => ApiKeysService.disable(apiKey!.id, body), {
    onSuccess: () => {
      setReason("");
      onSuccess();
    },
  });

  if (!apiKey) return null;

  const handleClose = () => {
    reset();
    setReason("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: DisableApiKeyRequest = {};
    const trimmed = reason.trim();
    if (trimmed) body.reason = trimmed;
    await mutate(body);
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("apiKeys.disableDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("apiKeys.disableDialog.description", { name: apiKey.name })}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <FormField
            label={t("apiKeys.disableDialog.reasonLabel")}
            value={reason}
            onChange={setReason}
            placeholder={t("apiKeys.disableDialog.reasonPlaceholder")}
            maxLength={500}
            hint={t("apiKeys.disableDialog.reasonHint")}
          />
          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("apiKeys.disableDialog.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? t("apiKeys.disableDialog.submitting")
              : t("apiKeys.disableDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
